import React, { useEffect, useState, useCallback } from "react";
import { useFilterOptions } from "../hooks/useFilterOptions";
import {
  Box,
  AppBar,
  Toolbar,
  TextField,
  IconButton,
  Button,
} from "@mui/material";
import MultiSelectFilter from "./MultiSelectFilter";
import { Search as SearchIcon, Clear as ClearIcon } from "@mui/icons-material";

export interface FilterConfig {
  showAcctgDt?: boolean;
  showPlanningArea?: boolean;
  showTechLdap?: boolean;
  showQuotedJobcode?: boolean;
  showDeclineReason?: boolean;
  showLlmClassification?: boolean;
  showCreateSource?: boolean;
  showApplianceType?: boolean;
  showOrderNumber?: boolean;
  showCustomerRating?: boolean;
}

export interface FilterValues {
  acctgDt: string[];
  planningArea: string[];
  techLdap: string[];
  quotedJobcode: string[];
  declineReason: string[];
  llmClassification: string[];
  createSource: string[];
  applianceType: string[];
  orderNumber: string;
  customerRating: string[];
}

interface FilterBarProps {
  config: FilterConfig;
  initialValues?: FilterValues;
  onFilterChange: (filters: FilterValues) => void;
}

const FilterBar: React.FC<FilterBarProps> = ({
  config,
  initialValues,
  onFilterChange,
}) => {
  // Use custom hook for filter options
  const {
    areas,
    jobcodes,
    techLdaps,
    acctgDates,
    declineReasons,
    applianceTypes,
    createSources,
    llmChatClassifications,
    customerRatings,
    loading: loadingFilters,
  } = useFilterOptions(config);

  // Selected filter values - initialize from initialValues if provided
  const [selectedAcctgDates, setSelectedAcctgDates] = useState<string[]>(
    initialValues?.acctgDt || []
  );
  const [selectedAreas, setSelectedAreas] = useState<string[]>(
    initialValues?.planningArea || []
  );
  const [selectedTechLdaps, setSelectedTechLdaps] = useState<string[]>(
    initialValues?.techLdap || []
  );
  const [selectedJobcodes, setSelectedJobcodes] = useState<string[]>(
    initialValues?.quotedJobcode || []
  );
  const [selectedDeclineReasons, setSelectedDeclineReasons] = useState<
    string[]
  >(initialValues?.declineReason || []);
  const [selectedLlmClassifications, setSelectedLlmClassifications] = useState<
    string[]
  >(initialValues?.llmClassification || []);
  const [selectedCreateSources, setSelectedCreateSources] = useState<string[]>(
    initialValues?.createSource || []
  );
  const [selectedApplianceTypes, setSelectedApplianceTypes] = useState<
    string[]
  >(initialValues?.applianceType || []);
  const [searchOrderNumber, setSearchOrderNumber] = useState<string>(
    initialValues?.orderNumber || ""
  );
  const [selectedCustomerRatings, setSelectedCustomerRatings] = useState<
    string[]
  >(initialValues?.customerRating || []);

  // Search text for filtering dropdown options
  const [searchAcctgDt, setSearchAcctgDt] = useState<string>("");
  const [searchArea, setSearchArea] = useState<string>("");
  const [searchTechLdap, setSearchTechLdap] = useState<string>("");
  const [searchJobcode, setSearchJobcode] = useState<string>("");
  const [searchDeclineReason, setSearchDeclineReason] = useState<string>("");
  const [searchLlmClassification, setSearchLlmClassification] =
    useState<string>("");
  const [searchCreateSource, setSearchCreateSource] = useState<string>("");
  const [searchApplianceType, setSearchApplianceType] = useState<string>("");
  const [searchCustomerRating, setSearchCustomerRating] = useState<string>("");

  // Temporary selections (before OK is clicked)
  const [tempAcctgDates, setTempAcctgDates] = useState<string[]>([]);
  const [tempAreas, setTempAreas] = useState<string[]>([]);
  const [tempTechLdaps, setTempTechLdaps] = useState<string[]>([]);
  const [tempJobcodes, setTempJobcodes] = useState<string[]>([]);
  const [tempDeclineReasons, setTempDeclineReasons] = useState<string[]>([]);
  const [tempLlmClassifications, setTempLlmClassifications] = useState<
    string[]
  >([]);
  const [tempCreateSources, setTempCreateSources] = useState<string[]>([]);
  const [tempApplianceTypes, setTempApplianceTypes] = useState<string[]>([]);
  const [tempCustomerRatings, setTempCustomerRatings] = useState<string[]>([]);

  // Sync selected values when initialValues changes
  useEffect(() => {
    if (initialValues) {
      setSelectedAcctgDates(initialValues.acctgDt || []);
      setSelectedAreas(initialValues.planningArea || []);
      setSelectedTechLdaps(initialValues.techLdap || []);
      setSelectedJobcodes(initialValues.quotedJobcode || []);
      setSelectedDeclineReasons(initialValues.declineReason || []);
      setSelectedLlmClassifications(initialValues.llmClassification || []);
      setSelectedCreateSources(initialValues.createSource || []);
      setSelectedApplianceTypes(initialValues.applianceType || []);
      setSearchOrderNumber(initialValues.orderNumber || "");
      setSelectedCustomerRatings(initialValues.customerRating || []);
    }
  }, [initialValues]);

  // Helper function to apply filters - call immediately when OK is clicked
  const applyFilters = useCallback((updates: Partial<FilterValues>) => {
    onFilterChange({
      acctgDt:
        updates.acctgDt !== undefined ? updates.acctgDt : selectedAcctgDates,
      planningArea:
        updates.planningArea !== undefined
          ? updates.planningArea
          : selectedAreas,
      techLdap:
        updates.techLdap !== undefined ? updates.techLdap : selectedTechLdaps,
      quotedJobcode:
        updates.quotedJobcode !== undefined
          ? updates.quotedJobcode
          : selectedJobcodes,
      declineReason:
        updates.declineReason !== undefined
          ? updates.declineReason
          : selectedDeclineReasons,
      llmClassification:
        updates.llmClassification !== undefined
          ? updates.llmClassification
          : selectedLlmClassifications,
      createSource:
        updates.createSource !== undefined
          ? updates.createSource
          : selectedCreateSources,
      applianceType:
        updates.applianceType !== undefined
          ? updates.applianceType
          : selectedApplianceTypes,
      orderNumber:
        updates.orderNumber !== undefined
          ? updates.orderNumber
          : searchOrderNumber,
      customerRating:
        updates.customerRating !== undefined
          ? updates.customerRating
          : selectedCustomerRatings,
    });
  }, [
    onFilterChange,
    selectedAcctgDates,
    selectedAreas,
    selectedTechLdaps,
    selectedJobcodes,
    selectedDeclineReasons,
    selectedLlmClassifications,
    selectedCreateSources,
    selectedApplianceTypes,
    searchOrderNumber,
    selectedCustomerRatings,
  ]);

  // Handle search for Order Number only
  const handleSearchOrder = useCallback(() => {
    applyFilters({ orderNumber: searchOrderNumber });
  }, [applyFilters, searchOrderNumber]);

  const handleClearAll = useCallback(() => {
    setSelectedAcctgDates([]);
    setSelectedAreas([]);
    setSelectedTechLdaps([]);
    setSelectedJobcodes([]);
    setSelectedDeclineReasons([]);
    setSelectedLlmClassifications([]);
    setSelectedCreateSources([]);
    setSelectedApplianceTypes([]);
    setSearchOrderNumber("");
    setSelectedCustomerRatings([]);

    setTempAcctgDates([]);
    setTempAreas([]);
    setTempTechLdaps([]);
    setTempJobcodes([]);
    setTempDeclineReasons([]);
    setTempLlmClassifications([]);
    setTempCreateSources([]);
    setTempApplianceTypes([]);
    setTempCustomerRatings([]);

    setSearchAcctgDt("");
    setSearchArea("");
    setSearchTechLdap("");
    setSearchJobcode("");
    setSearchDeclineReason("");
    setSearchLlmClassification("");
    setSearchCreateSource("");
    setSearchApplianceType("");
    setSearchCustomerRating("");

    // Clear filters immediately
    onFilterChange({
      acctgDt: [],
      planningArea: [],
      techLdap: [],
      quotedJobcode: [],
      declineReason: [],
      llmClassification: [],
      createSource: [],
      applianceType: [],
      orderNumber: "",
      customerRating: [],
    });
  }, [onFilterChange]);

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: "white",
        boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
      }}
    >
      <Toolbar sx={{ minHeight: "auto !important", px: 2, py: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            width: "100%",
            alignItems: "center",
          }}
        >
          {config.showAcctgDt && (
            <MultiSelectFilter
              label="Decline Date"
              labelId="acctg-dt-label"
              options={acctgDates}
              selectedValues={selectedAcctgDates}
              tempValues={tempAcctgDates}
              searchText={searchAcctgDt}
              onSearchChange={setSearchAcctgDt}
              onTempChange={setTempAcctgDates}
              loading={loadingFilters}
              onOk={() => {
                setSelectedAcctgDates(tempAcctgDates);
                setSearchAcctgDt("");
                applyFilters({ acctgDt: tempAcctgDates });
              }}
              onCancel={() => {
                setTempAcctgDates(selectedAcctgDates);
                setSearchAcctgDt("");
              }}
              onOpen={() => {
                setTempAcctgDates(selectedAcctgDates);
                setSearchAcctgDt("");
              }}
            />
          )}

          {config.showPlanningArea && (
            <MultiSelectFilter
              label="Planning Area"
              labelId="planning-area-label"
              options={areas}
              selectedValues={selectedAreas}
              tempValues={tempAreas}
              searchText={searchArea}
              onSearchChange={setSearchArea}
              onTempChange={setTempAreas}
              loading={loadingFilters}
              onOk={() => {
                setSelectedAreas(tempAreas);
                setSearchArea("");
                applyFilters({ planningArea: tempAreas });
              }}
              onCancel={() => {
                setTempAreas(selectedAreas);
                setSearchArea("");
              }}
              onOpen={() => {
                setTempAreas(selectedAreas);
                setSearchArea("");
              }}
            />
          )}

          {config.showTechLdap && (
            <MultiSelectFilter
              label="Tech ID"
              labelId="tech-ldap-label"
              options={techLdaps}
              selectedValues={selectedTechLdaps}
              tempValues={tempTechLdaps}
              searchText={searchTechLdap}
              onSearchChange={setSearchTechLdap}
              onTempChange={setTempTechLdaps}
              loading={loadingFilters}
              onOk={() => {
                setSelectedTechLdaps(tempTechLdaps);
                setSearchTechLdap("");
                applyFilters({ techLdap: tempTechLdaps });
              }}
              onCancel={() => {
                setTempTechLdaps(selectedTechLdaps);
                setSearchTechLdap("");
              }}
              onOpen={() => {
                setTempTechLdaps(selectedTechLdaps);
                setSearchTechLdap("");
              }}
            />
          )}

          {config.showQuotedJobcode && (
            <MultiSelectFilter
              label="Quoted Jobcode"
              labelId="quoted-jobcode-label"
              options={jobcodes}
              selectedValues={selectedJobcodes}
              tempValues={tempJobcodes}
              searchText={searchJobcode}
              onSearchChange={setSearchJobcode}
              onTempChange={setTempJobcodes}
              loading={loadingFilters}
              onOk={() => {
                setSelectedJobcodes(tempJobcodes);
                setSearchJobcode("");
                applyFilters({ quotedJobcode: tempJobcodes });
              }}
              onCancel={() => {
                setTempJobcodes(selectedJobcodes);
                setSearchJobcode("");
              }}
              onOpen={() => {
                setTempJobcodes(selectedJobcodes);
                setSearchJobcode("");
              }}
            />
          )}

          {config.showCustomerRating && (
            <MultiSelectFilter
              label="Customer Rating"
              labelId="customer-rating-label"
              options={customerRatings}
              selectedValues={selectedCustomerRatings}
              tempValues={tempCustomerRatings}
              searchText={searchCustomerRating}
              onSearchChange={setSearchCustomerRating}
              onTempChange={setTempCustomerRatings}
              menuWidth={300}
              loading={loadingFilters}
              onOk={() => {
                setSelectedCustomerRatings(tempCustomerRatings);
                setSearchCustomerRating("");
                applyFilters({ customerRating: tempCustomerRatings });
              }}
              onCancel={() => {
                setTempCustomerRatings(selectedCustomerRatings);
                setSearchCustomerRating("");
              }}
              onOpen={() => {
                setTempCustomerRatings(selectedCustomerRatings);
                setSearchCustomerRating("");
              }}
            />
          )}

          {config.showDeclineReason && (
            <MultiSelectFilter
              label="Decline Reason"
              labelId="decline-reason-label"
              options={declineReasons}
              selectedValues={selectedDeclineReasons}
              tempValues={tempDeclineReasons}
              searchText={searchDeclineReason}
              onSearchChange={setSearchDeclineReason}
              onTempChange={setTempDeclineReasons}
              loading={loadingFilters}
              onOk={() => {
                setSelectedDeclineReasons(tempDeclineReasons);
                setSearchDeclineReason("");
                applyFilters({ declineReason: tempDeclineReasons });
              }}
              onCancel={() => {
                setTempDeclineReasons(selectedDeclineReasons);
                setSearchDeclineReason("");
              }}
              onOpen={() => {
                setTempDeclineReasons(selectedDeclineReasons);
                setSearchDeclineReason("");
              }}
            />
          )}

          {config.showCreateSource && (
            <MultiSelectFilter
              label="Create Source"
              labelId="create-source-label"
              options={createSources}
              selectedValues={selectedCreateSources}
              tempValues={tempCreateSources}
              searchText={searchCreateSource}
              onSearchChange={setSearchCreateSource}
              onTempChange={setTempCreateSources}
              loading={loadingFilters}
              onOk={() => {
                setSelectedCreateSources(tempCreateSources);
                setSearchCreateSource("");
                applyFilters({ createSource: tempCreateSources });
              }}
              onCancel={() => {
                setTempCreateSources(selectedCreateSources);
                setSearchCreateSource("");
              }}
              onOpen={() => {
                setTempCreateSources(selectedCreateSources);
                setSearchCreateSource("");
              }}
            />
          )}

          {config.showApplianceType && (
            <MultiSelectFilter
              label="Appliance Type"
              labelId="appliance-type-label"
              options={applianceTypes}
              selectedValues={selectedApplianceTypes}
              tempValues={tempApplianceTypes}
              searchText={searchApplianceType}
              onSearchChange={setSearchApplianceType}
              onTempChange={setTempApplianceTypes}
              loading={loadingFilters}
              onOk={() => {
                setSelectedApplianceTypes(tempApplianceTypes);
                setSearchApplianceType("");
                applyFilters({ applianceType: tempApplianceTypes });
              }}
              onCancel={() => {
                setTempApplianceTypes(selectedApplianceTypes);
                setSearchApplianceType("");
              }}
              onOpen={() => {
                setTempApplianceTypes(selectedApplianceTypes);
                setSearchApplianceType("");
              }}
            />
          )}

          {config.showLlmClassification && (
            <MultiSelectFilter
              label="Create Source Classification"
              labelId="llm-classification-label"
              options={llmChatClassifications}
              selectedValues={selectedLlmClassifications}
              tempValues={tempLlmClassifications}
              searchText={searchLlmClassification}
              onSearchChange={setSearchLlmClassification}
              onTempChange={setTempLlmClassifications}
              menuWidth={600}
              loading={loadingFilters}
              onOk={() => {
                setSelectedLlmClassifications(tempLlmClassifications);
                setSearchLlmClassification("");
                applyFilters({ llmClassification: tempLlmClassifications });
              }}
              onCancel={() => {
                setTempLlmClassifications(selectedLlmClassifications);
                setSearchLlmClassification("");
              }}
              onOpen={() => {
                setTempLlmClassifications(selectedLlmClassifications);
                setSearchLlmClassification("");
              }}
            />
          )}

          {config.showOrderNumber && (
            <Box
              sx={{ display: "flex", alignItems: "center", flex: 1, gap: 0 }}
            >
              <TextField
                size="small"
                value={searchOrderNumber}
                onChange={(e) => setSearchOrderNumber(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleSearchOrder();
                  }
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 28,
                    bgcolor: "white",
                    borderRadius: "4px 0 0 4px",
                  },
                  "& .MuiInputBase-input": {
                    py: 0.25,
                    fontSize: "0.875rem",
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#d0d0d0",
                      borderRight: "none",
                    },
                    "&:hover fieldset": {
                      borderColor: "#b0b0b0",
                      borderRight: "none",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1976d2",
                      borderRight: "none",
                    },
                  },
                }}
                placeholder="Enter Order Number..."
              />
              <Button
                onClick={handleSearchOrder}
                sx={{
                  bgcolor: "#1976d2",
                  color: "white",
                  minWidth: "36px",
                  height: 28,
                  borderRadius: "0 4px 4px 0",
                  px: 1.25,
                  minHeight: "auto",
                  "&:hover": {
                    bgcolor: "#1565c0",
                  },
                }}
              >
                <SearchIcon sx={{ fontSize: 18 }} />
              </Button>
            </Box>
          )}

          {/* Clear All Button */}
          <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
            <IconButton
              onClick={handleClearAll}
              sx={{
                bgcolor: "#f44336",
                color: "white",
                width: 36,
                height: 36,
                "&:hover": {
                  bgcolor: "#d32f2f",
                },
              }}
              title="Clear All Filters"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default FilterBar;
